import * as React from "react";
import { Button, Gapped, Modal } from "react-ui";
import styled from "@emotion/styled";

import { ILongOperationProgress } from "../IUIUtil";
import { useAsyncEffect } from "../../../../../../../../Common/UseAsyncEffect";
import { delay } from "../../../../../../../../Common/AsyncUtils";
import { theme } from "../../../../../../Controls/Internals/ThemeProvider/CandyTheme/ThemeShorthand";
import { clamp } from "../../../../../../../../Common/IntRangeUtils";

interface LongOperationModalProps {
    title: string;
    allowCancel: boolean;
    specialCase?: undefined | "NdflEmployeeImport" | "InfiniteProcess";
    executeCallback: (progress: ILongOperationProgress, signal: AbortSignal) => Promise<void>;
    onClose: () => void;
    onError: (e: unknown) => void;
}

export function LongOperationModal(props: LongOperationModalProps): JSX.Element {
    const [finishValue, setFinishValue] = React.useState(100);
    const [currentProgress, setCurrentProgress] = React.useState(0);
    const [confirmCancelModal, setConfirmCancelModal] = React.useState(false);
    const [canceling, setCanceling] = React.useState(false);
    const [cancelAbortController, setCancelAbortController] = React.useState<AbortController | undefined>(undefined);

    useAsyncEffect(async () => {
        await delay(100);
        const abortController = new AbortController();
        setCancelAbortController(abortController);
        try {
            await props.executeCallback(
                {
                    setFinishValue: setFinishValue,
                    reportProgress: setCurrentProgress,
                },
                abortController.signal
            );
            props.onClose();
        } catch (e) {
            props.onError(e);
        }
    }, []);

    const handleBeginCancel = () => {
        setConfirmCancelModal(true);
    };

    const handleConfigCancel = () => {
        setConfirmCancelModal(false);
        cancelAbortController?.abort();
        setCanceling(true);
    };

    const handleDiscardCancel = () => {
        setConfirmCancelModal(false);
    };

    const BodyComponent =
        props.specialCase === "NdflEmployeeImport"
            ? NdflImportEmployeeBody
            : props.specialCase === "InfiniteProcess"
              ? InfiniteProcessBody
              : DefaultBody;

    return (
        <>
            <Modal ignoreBackgroundClick noClose>
                <Modal.Header>{props.title}</Modal.Header>
                <Modal.Body>
                    <BodyComponent currentProgress={currentProgress} finishValue={finishValue} canceling={canceling} />
                </Modal.Body>
                <Modal.Footer>
                    {props.allowCancel && (
                        <Button
                            use="danger"
                            size="medium"
                            onClick={handleBeginCancel}
                            disabled={canceling}
                            loading={canceling}>
                            Отменить
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
            {confirmCancelModal && (
                <Modal>
                    <Modal.Header>Отменить операцию?</Modal.Header>
                    <Modal.Footer>
                        <Gapped gap={8}>
                            <Button use="danger" size={"medium"} onClick={handleConfigCancel}>
                                Отменить
                            </Button>
                            <Button size={"medium"} onClick={handleDiscardCancel}>
                                Не отменять
                            </Button>
                        </Gapped>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
}

interface BodyProps {
    finishValue: number;
    currentProgress: number;
    canceling: boolean;
}

function DefaultBody(props: BodyProps): JSX.Element {
    return (
        <div>
            {props.canceling ? "Отмена..." : `Завершено: ${props.currentProgress} / ${props.finishValue}`}
            <Progress dimmed={props.canceling}>
                <ProgressLoading
                    style={{
                        width: `${clamp(Math.round((props.currentProgress * 100) / props.finishValue), 0, 100)}%`,
                    }}
                />
            </Progress>
        </div>
    );
}

function InfiniteProcessBody(props: BodyProps): JSX.Element {
    return (
        <div>
            <div>Выполняется операция. Ожидайте..</div>
            <Progress dimmed={props.canceling}>
                <ProgressLoading
                    style={{
                        width: `100%`,
                    }}
                />
            </Progress>
        </div>
    );
}

function NdflImportEmployeeBody(props: BodyProps): JSX.Element {
    return (
        <ImportBody>
            Добавление большого количества сотрудников может занять длительное время.
            <br />
            <br />
            {props.canceling ? "Отмена..." : `Завершено: ${props.currentProgress} / ${props.finishValue}`}
            <Progress dimmed={props.canceling}>
                <ProgressLoading
                    style={{
                        width: `${clamp(Math.round((props.currentProgress * 100) / props.finishValue), 0, 100)}%`,
                    }}
                />
            </Progress>
        </ImportBody>
    );
}

export const ImportBody = styled.div(() => ({
    width: 400,
}));

export const ProgressLoading = styled.div(props => ({
    height: 4,
    backgroundColor: theme(props).semanticColors.action.alternativeColor,
}));

export const Progress = styled.div<{ dimmed?: boolean }>(props => ({
    height: 4,
    backgroundColor: theme(props).keyPalette.panel2BackgroundColor,
    boxSizing: "border-box",
    width: 400,

    [`${ProgressLoading}`]: {
        backgroundColor: props.dimmed ? theme(props).keyPalette.panel2BackgroundColorAsBlackTransparent : undefined,
    },
}));
